import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { type Option, SearchSelect } from '~/styles/forms/SearchSelect';
import { formatString } from '~/utils/stringFormat';
import {
  type BoardTarget,
  InfoHeading,
  InfoTable,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  TargetSelect,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';
import {
  type AssignCandidate,
  PEOPLE_PICKER_DEBOUNCE_MS,
  useAssignCandidates,
} from './useAssignCandidates';
import { useStudentAdoptionInfo } from './useStudentAdoptionInfo';

interface AssignStudentModalProps {
  /** The student to assign. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the assignment has landed, so the board can refresh behind it. */
  onAssigned?: () => void;
}

interface AssignedSummary {
  studentName: string;
  studyHelperName: string;
}

/**
 * The two-step assign popup.
 *
 * The body is two info tables: the student information block the adopt modal
 * shows, then the coordinator's name and the study helper picker.
 *
 * The picker is bound to the web target above it: the roster is keyed on the
 * target, and changing the target clears the chosen helper. Otherwise the
 * confirm could post group Y's web target with group X's leader, which the
 * server refuses.
 */
export function AssignStudentModal({
  studentId,
  onClose,
  onAssigned,
}: AssignStudentModalProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const [assigned, setAssigned] = useState<AssignedSummary | null>(null);
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  // Held as id AND name: the success sentence names the helper, and by the time
  // it renders the roster query may have been re-run under a different search
  // term and no longer carry the row that was picked.
  const [candidate, setCandidate] = useState<AssignCandidate | null>(null);
  // What's typed, and the debounced term the roster query is keyed on.
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  /** Which target the current candidate was chosen against — see the reset below. */
  const [candidateFor, setCandidateFor] = useState<string | null>(null);
  // Which student the modal is showing now, readable from a settled request;
  // see `AdoptStudentModal`.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late,
  // and a second confirm would adopt a student who is already off the board.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body, target choice or candidate. Adjusting state during render (rather than
  // in an effect) keeps the first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setAssigned(null);
    setSelectedTargetId(null);
    setCandidate(null);
    setTerm('');
    setSearch('');
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(term), PEOPLE_PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  // `mode=assign` runs no adoption checks, since no helper is chosen yet, but
  // applies the coordinator gate and narrows the web targets to groups this
  // caller admins (adopt allows mod or admin).
  const { info, isLoading, error } = useStudentAdoptionInfo(
    studentId,
    assigned === null,
    'assign',
  );
  const { mutateAsync: assignStudent, isPending } =
    api.assignStudent.useMutation();

  // The original targets, not the available ones, as in legacy; a matched
  // campaign hides the web target select entirely (the rule `StudentInfoBlock`
  // enforces).
  const targets = info?.original_targets ?? [];
  const postedTargetId =
    targets.find((target) => target.target_id === selectedTargetId)
      ?.target_id ??
    targets[0]?.target_id ??
    null;
  const campaignId = info?.matched_campaign?.campaign_id;
  const webAdId =
    campaignId === undefined && postedTargetId !== null
      ? postedTargetId
      : undefined;

  // Legacy's reset-on-change, done during render like the `openedFor` reset: an
  // effect would clear the choice a paint after the roster re-keyed, leaving
  // the previous group's candidate postable for a frame. The roster refetches
  // on its own, since both ids are in its query key.
  const targetKey =
    campaignId === undefined
      ? `web-ad:${postedTargetId}`
      : `campaign:${campaignId}`;
  if (candidateFor !== targetKey) {
    setCandidateFor(targetKey);
    setCandidate(null);
    setTerm('');
    setSearch('');
  }

  const {
    candidates,
    count: candidateCount,
    isFetching: candidatesFetching,
    error: candidatesError,
  } = useAssignCandidates(
    assigned === null ? studentId : null,
    campaignId,
    webAdId,
    search,
  );

  const candidateOptions = useMemo<Option[]>(
    () =>
      candidates.map((candidate) => ({
        value: String(candidate.id),
        // The id is in the label because two leaders can share a display name.
        label: `${candidate.display_name} (#${candidate.id})`,
      })),
    [candidates],
  );

  // A roster refusal fails the whole modal, not one control. It happens only
  // for a posted target outside the caller's assignable groups, e.g. a student
  // with both a study group campaign and a web target the campaign hides.
  // Better than a picker that silently stays empty.
  const failure = submitError ?? error ?? candidatesError;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  const handleClose = (): void => {
    setAssigned(null);
    setSubmitError(null);
    onClose();
  };

  // Success first: the adoption has landed, so nothing that follows can undo it.
  if (assigned !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.assignment-successful')}
        onClose={handleClose}
        footer={
          <WBSButton alternate onClick={handleClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {formatString(
            t('messages.s-successfully-assigned-to'),
            assigned.studentName,
            assigned.studyHelperName,
          )}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  // A typed 4xx from `/adoption-info` arrives instead of the payload, so the
  // modal opens straight into the error, as in legacy. Refusals from `POST
  // /assign` and the roster land in the same place.
  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.assign-to-your-study-helper')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.assign-to-your-study-helper')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  // No candidates at all, not no match for the search: the caller is excluded
  // from their own roster, so a group with one admin has none.
  const rosterEmpty =
    search === '' && !candidatesFetching && candidateCount === 0;

  const handleAssign = async (): Promise<void> => {
    if (submitting.current || candidate === null) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      await assignStudent({
        body: {
          user_id: studentId,
          study_helper_id: candidate.id,
          // Exactly one attribution, or neither, as in the adopt modal. A
          // matched campaign hides the target picker, and posting both is
          // `AdoptionTargetConflictError`.
          campaign_id: campaignId,
          web_ad_id: webAdId,
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onAssigned?.();
    if (openSession.current === submittedFor) {
      setAssigned({
        studentName: info.display_name,
        studyHelperName: candidate.display_name,
      });
    }
  };

  return (
    <StudentBoardModal
      title={t('wbs.assign-to-your-study-helper')}
      onClose={onClose}
      overflowVisible
      footer={
        // Hidden rather than disabled when there's nobody to assign to; legacy
        // shows an empty picker and lets the confirm fail. With candidates, the
        // button waits for a choice.
        rosterEmpty ? undefined : (
          <WBSButton
            onClick={() => {
              void handleAssign();
            }}
            isLoading={isPending}
            disabled={candidate === null}
          >
            {/* Legacy's confirm label is Assign, but there's no
               `wbs.assign` key yet, so this uses the nearest translated
               one. Not `wbs.assign-to` (Assign to...), the row action's
               label, or two buttons on screen would share an accessible
               name. */}
            {t('wbs.assign-to-2')}
          </WBSButton>
        )
      }
    >
      <ModalParagraph>{t('wbs.as-a-group-coordinator-you-can')}</ModalParagraph>
      <StudentInfoBlock
        info={info}
        targets={targets}
        renderTargets={(options: readonly BoardTarget[]) => (
          <TargetSelect
            aria-label={t('wbs.matched-web-ad-targets')}
            value={String(postedTargetId ?? '')}
            onChange={(event) =>
              setSelectedTargetId(Number(event.target.value))
            }
          >
            {options.map((target) => (
              <option key={target.target_id} value={String(target.target_id)}>
                {target.has_passed_on
                  ? `${targetLabel(target)} ${t('wbs.passed-on-suffix')}`
                  : targetLabel(target)}
              </option>
            ))}
          </TargetSelect>
        )}
      />
      <InfoHeading>{t('wbs.assign-information')}</InfoHeading>
      <InfoTable>
        <tbody>
          <tr>
            <th>{t('wbs.assign-name')}</th>
            {/* The caller, who is never posted: the server takes the
               assigner from the session. */}
            <td>{me?.display_name ?? ''}</td>
          </tr>
          <tr>
            {/* The colon-bearing key, matching the row above. */}
            <th>{t('lessons.study-helper-2')}</th>
            <td>
              {rosterEmpty ? (
                <EmptyPickerNotice>
                  {t('wbs.no-study-helpers-to-assign')}
                </EmptyPickerNotice>
              ) : (
                <SearchSelect
                  multi={false}
                  value={candidate === null ? [] : [String(candidate.id)]}
                  options={candidateOptions}
                  onChange={(next) =>
                    setCandidate(
                      candidates.find((row) => String(row.id) === next[0]) ??
                        null,
                    )
                  }
                  // Server-side search with no minimum length: unlike legacy's
                  // picker, this opens on the group's first page of leaders.
                  onSearch={setTerm}
                  loading={term !== search || candidatesFetching}
                  placeholder={t('wbs.select-a-value')}
                />
              )}
            </td>
          </tr>
        </tbody>
      </InfoTable>
    </StudentBoardModal>
  );
}

/**
 * Legacy shows nothing here, so this is new, English-only copy
 * (`wbs.no-study-helpers-to-assign`).
 */
const EmptyPickerNotice = styled.span`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 14px;
`;
